import cn from "classnames";
import { ymEvent } from "helpers/system";
import { divIcon } from "leaflet";
import debounce from "lodash/debounce";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Circle, Tooltip as MapTooltip, Marker, Polygon, Polyline, Popup } from "react-leaflet";
import { useMapEvents } from "react-leaflet/hooks";
import { alert } from "services/alert";
import { checkPolygonArea, geocoder, getMixedAddresses, getOrganizationAdresses } from "sources/map";
import { AddressInput, Map as SharedMap } from "./shared";

const POLYGON_MAX_POINTS_COUNT = 50;

const digitsOnly = (value) => `${value ?? ""}`.replace(/\D/g, "");
const formatRadius = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const radius = parseInt(value, 10);
    return Number.isNaN(radius) ? null : radius;
};

const kindDependedData = {
    address: {
        label: "По адресу",
        placeholder: "Начните вводить адрес или нажмите на карту",
        source: getMixedAddresses,
    },
    organizations: {
        label: "По организациям",
        placeholder: "Введите название организации",
        source: getOrganizationAdresses,
    },
};

const typeDependedData = {
    radius: {
        label: "Круг",
        placeholder: () => "Радиус зоны фиксации въезда (в метрах)",
    },
    polygon: {
        label: "Произвольная зона",
        placeholder: (pointCount) =>
            pointCount > 0
                ? `Выбрана область из ${pointCount} вершин`
                : "Нарисуйте область на карте для фиксации въезда",
    },
};

const MapClickHandler = (props) => {
    useMapEvents({
        click: props.onClick,
        mousemove: props.onMove,
    });
    return null;
};

export default function PickerMap(props) {
    const { data, history, changeCenter, tileUrls, viewport, mergeMapViewport, children } = props;
    const [geopoint, setGeopoint] = useState(data.value || { location: { coordinates: [] } });
    const [selectValue, setSelectValue] = useState("address");
    const [selectOpened, setSelectOpened] = useState(false);
    const [typeValue, setTypeValue] = useState(data.value?.coordinates?.length ? "polygon" : "radius");
    const [typeOpened, setTypeOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [radius, setRadius] = useState(data.value?.radius);
    const [buildPolygonMode, setBuildPolygonMode] = useState(false);
    const [buildRadiusMode, setBuildRadiusMode] = useState(false);
    const [polygonPoints, setPolygonPoints] = useState(
        data.value?.coordinates?.map((item) => [...item].reverse()) ?? [],
    );

    const firstPointRef = useRef(null);

    useEffect(() => {
        if (data.value) {
            const { coordinates } = data.value.location;
            if (coordinates.length) {
                changeCenter([...coordinates].reverse());
            }
        }
    }, []);

    useEffect(() => {
        const unlisten = history.listen(() => cancel());
        return unlisten;
    }, []);

    useEffect(() => {
        firstPointRef.current?.openPopup();
    }, [polygonPoints]);

    const cancel = useCallback(() => {
        data.callback(null);
    }, [data]);

    const handleMapClick = (event) => {
        if (buildPolygonMode) {
            const { latlng } = event;
            // нужна еще одна точка для замыкания полигона
            if (polygonPoints.length === POLYGON_MAX_POINTS_COUNT - 1) {
                return alert({
                    message: "Полигон не может содержать более 50 вершин, замкните область или удалите лишние вершины",
                    type: "warning",
                });
            }
            setPolygonPoints((points) => [...points, [latlng.lat, latlng.lng]]);
        } else if (buildRadiusMode) {
            setBuildRadiusMode(false);
        } else {
            createMarker(event);
        }
    };

    const handleMapMove = (event) => {
        if (!buildRadiusMode) {
            return;
        }
        if (geopoint.location.coordinates.length) {
            const distance = event.latlng.distanceTo({
                lat: geopoint.location.coordinates[1],
                lng: geopoint.location.coordinates[0],
            });
            setRadius(distance);
        }
    };

    const createMarker = useCallback(
        debounce(({ latlng }) => {
            setIsLoading(true);
            changeCenter([latlng.lat, latlng.lng]);
            geocoder(latlng)
                .then((geo) => {
                    if (geo) {
                        setGeopoint(geo);
                    }
                })
                .finally(() => setIsLoading(false));
        }, 100),
        [],
    );

    const confirmMarker = () => {
        if (buildPolygonMode) {
            return alert({ message: "Выберите облась для фиксации въезда на точку", type: "warning" });
        }
        if (geopoint.location.coordinates.length) {
            const formattedRadius = formatRadius(radius);
            const callbackAction = () => {
                data.callback({
                    name: "",
                    ...geopoint,
                    ...(typeValue === "radius"
                        ? { radius: formattedRadius, coordinates: null }
                        : { coordinates: polygonPoints.map((item) => [...item].reverse()), radius: null }),
                });
            };
            if (typeValue === "polygon") {
                ymEvent("PointPolygonCreated");
                checkPolygonArea({ coordinates: polygonPoints.map((item) => [...item].reverse()), radius: null }).then(
                    (res) => {
                        if (res.isValid) {
                            callbackAction();
                        } else {
                            alert({
                                message: `Вы выбрали территорию в ${(res.area / 1_000_000).toFixed(1)}км², что превышает допустимый максимум в 1км². Пожалуйста, скорректируйте размер области.`,
                                type: "warning",
                            });
                        }
                    },
                );
            } else {
                callbackAction();
            }
        } else {
            alert({ message: "Выберите более точный адрес", type: "warning" });
        }
    };

    const renderMarker = () => {
        if (!geopoint || !geopoint.location) {
            return null;
        }
        const { coordinates } = geopoint.location;
        if (!coordinates || !coordinates.length) {
            return null;
        }
        const className = "map__icon map-icon map-icon--green-marker";
        const inverted = [coordinates[1], coordinates[0]];
        const options = { position: inverted, icon: divIcon({ iconSize: [2, 2], className }) };
        const formattedRadius = formatRadius(radius);
        return (
            <>
                <Marker {...options} />
                {formattedRadius !== null && typeValue === "radius" && (
                    <Circle center={inverted} radius={formattedRadius} />
                )}
            </>
        );
    };

    const onSelectGeopoint = useCallback(
        (geo) => {
            const { coordinates } = geo.location;
            if (coordinates.length) {
                changeCenter([...coordinates].reverse());
            }
            setGeopoint(geo);
        },
        [changeCenter],
    );

    const changeSelectValue = (key) => {
        setSelectValue(key);
        setSelectOpened(false);
    };

    const changeTypeValue = (key) => {
        setTypeValue(key);
        setTypeOpened(false);
        if (key === "polygon") {
            setBuildPolygonMode(true);
        } else {
            setPolygonPoints([]);
        }
    };

    const closePolygon = () => {
        setPolygonPoints((points) => [...points, [polygonPoints[0][0], polygonPoints[0][1]]]);
        setTimeout(() => {
            setBuildPolygonMode(false);
        }, 0);
    };

    const resetPolygonPoints = () => {
        setTimeout(() => {
            setPolygonPoints([]);
        }, 0);
        setBuildPolygonMode(true);
    };

    const renderPolylines = () => {
        if (polygonPoints.length < 2) {
            return null;
        }
        return <Polyline positions={polygonPoints} />;
    };

    const handleDrag = (target, index) => {
        const { lng, lat } = target._latlng;
        setPolygonPoints((points) => points.map((item, i) => (i === index ? [lat, lng] : item)));
    };

    return (
        <div className="map__body-wrap">
            <div className="map__head">
                <div className="card card-body p-3 font-size-14">
                    <div className="d-flex gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex mb-2">
                                <div className="map__select-wrap" onClick={() => setSelectOpened(!selectOpened)}>
                                    {kindDependedData[selectValue].label}
                                    <i
                                        className={cn("map__select-icon", {
                                            "icon-uEA61-angle-down": !selectOpened,
                                            "icon-uEA62-angle-up": selectOpened,
                                        })}
                                    />
                                    {selectOpened && (
                                        <div className="map__select-options">
                                            {Object.entries(kindDependedData).map(([key, data]) => (
                                                <div
                                                    key={key}
                                                    className="map__select-options--item"
                                                    onClick={() => changeSelectValue(key)}
                                                >
                                                    {data.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="map__input-wrap">
                                    <AddressInput
                                        autofocus
                                        onSelectGeopoint={onSelectGeopoint}
                                        geopoint={geopoint}
                                        isLoading={isLoading}
                                        source={kindDependedData[selectValue].source}
                                        placeholder={kindDependedData[selectValue].placeholder}
                                    />
                                </div>
                            </div>
                            {!data.withoutRadius && geopoint?.location?.coordinates?.length > 0 && (
                                <div className="d-flex">
                                    <Tooltip
                                        overlay="Выберите тип области для фиксации въезда на точку"
                                        placement="top"
                                    >
                                        <div className="map__select-wrap" onClick={() => setTypeOpened(!typeOpened)}>
                                            {typeDependedData[typeValue].label}
                                            <i
                                                className={cn("map__select-icon", {
                                                    "icon-uEA61-angle-down": !typeOpened,
                                                    "icon-uEA62-angle-up": typeOpened,
                                                })}
                                            />
                                            {typeOpened && (
                                                <div className="map__select-options">
                                                    {Object.entries(typeDependedData).map(([key, data]) => (
                                                        <div
                                                            key={key}
                                                            className="map__select-options--item"
                                                            onClick={() => changeTypeValue(key)}
                                                        >
                                                            {data.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Tooltip>
                                    <div
                                        className={cn("map__input-wrap", {
                                            "map__input-wrap--disabled": typeValue === "polygon",
                                        })}
                                    >
                                        <input
                                            className="map__picker-field map__picker-field--desktop"
                                            value={typeValue === "polygon" ? "" : (formatRadius(radius) ?? "")}
                                            disabled={typeValue === "polygon"}
                                            onChange={(e) => setRadius(digitsOnly(e.target.value))}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            placeholder={typeDependedData[typeValue].placeholder(
                                                polygonPoints.length - (buildPolygonMode ? 0 : 1),
                                            )}
                                            onFocus={() => setBuildRadiusMode(true)}
                                            onKeyDown={() => setBuildRadiusMode(false)}
                                        />
                                        {typeValue === "polygon" && (
                                            <span className="cursor-pointer text-primary" onClick={resetPolygonPoints}>
                                                {buildPolygonMode ? "Сбросить" : "Изменить"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="map__button-wrap">
                            <div className="btn btn-brand map__submit-btn" onClick={confirmMarker}>
                                Подтвердить точку
                            </div>
                            <div className="btn btn-secondary map__cancel-btn mt-2" onClick={cancel}>
                                Отмена
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SharedMap viewport={viewport} onViewportChanged={mergeMapViewport} tileUrls={tileUrls} maxZoom={18}>
                <MapClickHandler onClick={handleMapClick} onMove={handleMapMove} />
                {renderMarker()}
                {polygonPoints.map((item, index) => {
                    const isLastLooped = !buildPolygonMode && [polygonPoints.length - 1, 0].includes(index);
                    const icon = divIcon({
                        className: cn("map-icon--unstable-zone-point cursor-move", {
                            "map-icon--unstable-zone-point-looped": isLastLooped,
                        }),
                        iconSize: [16, 16],
                    });
                    return (
                        <Marker
                            key={`${item.join(", ")}_${index}`}
                            ref={index === 0 ? firstPointRef : undefined}
                            draggable
                            position={item}
                            icon={icon}
                            eventHandlers={{
                                click: (event) => event.originalEvent.view.L.DomEvent.stopPropagation(event),
                                dblclick: (event) => {
                                    event.originalEvent.view.L.DomEvent.stopPropagation(event);
                                    const newPoints = [...polygonPoints];
                                    if (!buildPolygonMode && (index === 0 || index === polygonPoints.length - 1)) {
                                        newPoints.pop();
                                        setBuildPolygonMode(true);
                                    } else {
                                        newPoints.splice(index, 1);
                                    }
                                    setPolygonPoints(newPoints);
                                },
                                dragend: ({ target }) => {
                                    if (!buildPolygonMode && [0, polygonPoints.length - 1].includes(index)) {
                                        handleDrag(target, 0);
                                        handleDrag(target, polygonPoints.length - 1);
                                    } else {
                                        handleDrag(target, index);
                                    }
                                },
                            }}
                        >
                            {index === 0 && buildPolygonMode && (
                                <Popup
                                    className="map__popup mb-4"
                                    autoPan={false}
                                    closeOnClick={false}
                                    closeButton={false}
                                >
                                    <div className="p-2">
                                        <div className="pb-3 font-size-14">Замкнуть область?</div>
                                        <div className="d-flex justify-content-between">
                                            <div className="btn btn-xs btn-secondary" onClick={resetPolygonPoints}>
                                                Сбросить
                                            </div>
                                            <div className="btn btn-xs btn-brand" onClick={closePolygon}>
                                                Замкнуть
                                            </div>
                                        </div>
                                    </div>
                                </Popup>
                            )}
                            <MapTooltip>Переместите или нажмите дважды, чтобы удалить</MapTooltip>
                        </Marker>
                    );
                })}
                {renderPolylines()}
                {polygonPoints && !buildPolygonMode && <Polygon positions={polygonPoints} />}
                {children}
            </SharedMap>
        </div>
    );
}
